import { useMemo, useRef, useState } from 'react'
import TableMui from 'src/components/TableMui'
import { capitalizeString } from 'src/utils/capitalize'
import { useSelector } from 'src/utils/store'
import { Link } from '@mui/material'
import { useReactToPrint } from 'react-to-print'
import { useDispatch } from 'react-redux'
import { actions as stockManagementActions } from 'src/features/stock-management/StockManagementState'
import * as S from './CagesManagementTable.styles'
import { useHistory } from 'react-router-dom'
import { OpenInNew } from '@mui/icons-material'

const CagesManagementTable = () => {
  const { transferCages } = useSelector((state) => state.transfers)

  const dispatch = useDispatch()
  const history = useHistory()

  const [invoiceHtml, setInvoiceHtml] = useState<string>('')

  const printAreaRef = useRef(null)

  // eslint-disable-next-line react/no-danger
  const showInvoice = () => (<div style={{ pageBreakBefore: 'always' }} dangerouslySetInnerHTML={{ __html: invoiceHtml }} />)

  const printInvoice = useReactToPrint({
    content: () => printAreaRef.current,
    copyStyles: false,
  })

  const setInvoiceToPrint = (invoiceKey: string) => {
    setInvoiceHtml('')

    const onSuccess = (_invoiceHtml: string) => {
      setInvoiceHtml(_invoiceHtml)
      printInvoice()
    }

    dispatch(stockManagementActions.getInvoiceToPrint({ invoiceKey, onSuccess }))
  }

  const invoiceHyperlink = (invoice: string | undefined) => {
    if (invoice) {
      return (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <Link
          component='button'
          onClick={() => setInvoiceToPrint(invoice)}
          data-testid='invoice-hyperlink-test-id'
        >
          {invoice}
        </Link>
      )
    }
    return '-'
  }

  const receivementHyperlink = (receivementId: number | undefined) => {
    if (receivementId) {
      return (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <Link
          component='button'
          onClick={() => window.open(`/receivement/${receivementId}/details`, '_blank')}
          data-testid='receivement-hyperlink-test-id'
        >
          {receivementId}
        </Link>
      )
    }
    return '-'
  }

  const listHyperlink = (idList: number) => (
    // eslint-disable-next-line jsx-a11y/anchor-is-valid
    <Link
      component='button'
      onClick={() => window.open(`/transfers/manage/${idList}/pages`, '_blank')}
      data-testid='list-hyperlink-test-id'
    >
      {idList}
    </Link>
  )

  const pageHyperlink = (idPage: number) => (
    // eslint-disable-next-line jsx-a11y/anchor-is-valid
    <Link
      component='button'
      onClick={() => window.open(`/transfers/manage/page/${idPage}/details`, '_blank')}
      data-testid='page-hyperlink-test-id'
    >
      {idPage}
    </Link>
  )

  const openTransferCage = (idCage: number) => (
    <OpenInNew
      sx={{ cursor: 'pointer' }}
      onClick={(event) => {
        event.stopPropagation()
        history.push(`/transfers/cages/manage/cage/${idCage}/details`)
      }}
      data-testid='open-cage-button-test-id'
    />
  )

  const tableColumns = [
    { id: 'address', label: 'Endereço', testId: 'address-column-test-id' },
    { id: 'sourceFc', label: 'FC de origem', testId: 'source-fc-column-test-id' },
    { id: 'destinationFc', label: 'FC de destino', testId: 'destination-fc-column-test-id' },
    { id: 'qtySkus', label: 'Quantidade de SKUs', testId: 'qty-skus-column-test-id' },
    { id: 'status', label: 'Status', testId: 'status-column-test-id' },
    {
      id: 'startedAt', label: 'Iniciada em', testId: 'started-at-column-test-id', isDate: true,
    },
    { id: 'startedBy', label: 'Iniciada por', testId: 'started-by-column-test-id' },
    {
      id: 'updatedAt', label: 'Última atualização em', testId: 'updated-at-column-test-id', isDate: true,
    },
    { id: 'updatedBy', label: 'Última atualização por', testId: 'updated-by-column-test-id' },
    { id: 'invoiceAccessKey', label: 'Chave de acesso da nota', testId: 'invoice-access-key-column-test-id' },
    { id: 'nfeStatus', label: 'Status da NFe', testId: 'nfe-status-column-test-id' },
    { id: 'idReceivement', label: 'Recebimento', testId: 'id-receivement-column-test-id' },
    { id: 'idList', label: 'Lista', testId: 'id-list-column-test-id' },
    { id: 'idPage', label: 'Página', testId: 'id-page-column-test-id' },
    {
      id: 'open', label: 'Abrir', testId: 'open-column-test-id', useSorting: false,
    },
  ]

  const resolveTableRows = useMemo(() => transferCages.map((cages) => ({
    id: cages.idCage,
    address: cages.address,
    sourceFc: cages.sourceFc,
    destinationFc: cages.destinationFc,
    qtySkus: cages.qtySkus,
    status: capitalizeString(cages.status),
    startedAt: cages.createdAt,
    startedBy: capitalizeString(cages.firstOperator || '-'),
    updatedAt: cages.updatedAt,
    updatedBy: capitalizeString(cages.lastOperator || '-'),
    invoiceAccessKey: invoiceHyperlink(cages.invoiceAccessKey),
    nfeStatus: capitalizeString(cages.nfeStatus || '-'),
    idReceivement: receivementHyperlink(cages.idReceivement),
    idList: listHyperlink(cages.idList),
    idPage: pageHyperlink(cages.idPage),
    open: openTransferCage(cages.idCage),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [transferCages])

  return (
    <>
      <TableMui
        columns={tableColumns}
        emptyLabel='Utilize o filtro para buscar as gaiolas de transferência'
        rows={resolveTableRows}
        rowCount={resolveTableRows.length}
        enablePagination
        rowsPerPageOptions={[5, 10, 15, 20, 25, 30]}
        minHeight='60vh'
        maxHeight='60vh'
        hasSorting
        testId='transfer-cages-table-test-id'
      />
      <S.PrintArea ref={printAreaRef}>
        {showInvoice()}
      </S.PrintArea>
    </>
  )
}

export default CagesManagementTable
